/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-visit/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import { ASSIGN_VISIT_TO_ASSESS_COLUMNS } from '@components/admin/assign-visit/assign-visit.data';
import { AssignVisitDialogProps } from '@components/admin/assign-visit/assign-visit.interface';
import LibraryGrid from '@components/admin/library-grid';
import { useTranslation } from 'hooks';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISITS } from 'queries/study-visit-schedules';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import { StudySubjectVisitFields } from '@queries/generated/fragments.generated';
import {
    saveStudyVisitScheduleVisits,
    saveStudyVisitScheduleVisitsVariables,
} from '@queries/generated/study-visit-schedules.generated';

const AssignVisitDialog = ({ close, studyId, currentScheduleId, visits, assignedItem }: AssignVisitDialogProps) => {
    const { t } = useTranslation();

    const [selectedVisits, setSelectedVisits] = useState<StudySubjectVisitFields[]>([]);

    const [saveVisits, { loading: savingVisits }] = useMutation<
        saveStudyVisitScheduleVisits,
        saveStudyVisitScheduleVisitsVariables
    >(SAVE_STUDY_VISIT_SCHEDULE_VISITS);

    const { isCrf } = assignedItem;

    const getAvailableVisits = (items: StudySubjectVisitFields[]) => {
        const availableVisits = items.filter((visit) => visit.eventIds.indexOf(assignedItem.id) < 0);
        return orderBy(availableVisits, [{ field: 'visitName', dir: 'asc' }]);
    };

    const availableVisits = getAvailableVisits(visits);

    const onClose = (action?: string) => {
        setSelectedVisits([]);
        close(action);
    };

    const assignItems = (closeModal?: boolean) => {
        const requestArray = selectedVisits.map((selectedVisit) => ({
            id: selectedVisit.id,
            active: selectedVisit.active,
            sequenceNumber: selectedVisit.sequenceNumber,
            type: selectedVisit.type,
            visitId: selectedVisit.visitId,
            visitName: selectedVisit.visitName,
            leadVisitId: selectedVisit.leadVisitId,
            leadPeriod: selectedVisit.leadPeriod,
            leadUnit: selectedVisit.leadUnit,
            minWinPeriod: selectedVisit.minWinPeriod,
            maxWinPeriod: selectedVisit.maxWinPeriod,
            minWinUnit: selectedVisit.minWinUnit,
            minOccurrences: selectedVisit.minOccurrences,
            forecastEndDateField: selectedVisit.forecastEndDateField,
            isEndDateEarliest: selectedVisit.isEndDateEarliest,
            eventIds: [...selectedVisit.eventIds, assignedItem.id],
        }));

        saveVisits({
            variables: {
                studyId,
                visitScheduleId: currentScheduleId,
                visits: requestArray,
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: { studyId },
                },
            ],
        })
            .then(() => {
                setSelectedVisits([]);

                NotificationService.showNotification(
                    t(isCrf ? 'admin:successfulAssignedVisitsToCRF' : 'admin:successfulAssignedVisitsToAssessment', {
                        amount: selectedVisits.length,
                        id: assignedItem.recordId,
                    }),
                );

                if (closeModal) {
                    onClose();
                }
            })
            .catch(() => {
                onClose('error');
            });
    };

    return (
        <CustomDialog
            open
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            className="custom-width-popup max-w700"
        >
            <DialogTitle id="assign-visit-modal-title">
                {t('admin:assignVisitS')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="add-risk-dialog-content pb-0 mb-20">
                <div className="content-text">
                    <span className="title">{t(isCrf ? 'admin:crfTitle' : 'admin:assessmentTitle')}:</span>
                    {assignedItem.name}
                </div>
                <LibraryGrid
                    items={availableVisits}
                    columns={ASSIGN_VISIT_TO_ASSESS_COLUMNS(t)}
                    itemsType={t('admin:visitRecord-s')}
                    selection={{
                        selectedItems: selectedVisits,
                        setSelectedItems: setSelectedVisits,
                        showItemsCount: true,
                    }}
                />
            </DialogContent>

            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                <Button
                    text="ra:assignRisk"
                    disabled={!selectedVisits.length || savingVisits}
                    id="assign-visit-save"
                    onClick={() => assignItems(false)}
                />
                <Button
                    text="ra:assignAndClose"
                    disabled={!selectedVisits.length || savingVisits}
                    id="assign-visit-save-close"
                    onClick={() => assignItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignVisitDialog;
